'use client'

// --- UI ---
import { ContentItemMenu } from '@/tiptap/editor/components/ContentItemMenu'
import {
  Editor,
  EditorContent,
  EditorContext,
  EditorContextValue,
} from '@tiptap/react'
// --- UI ---
import { useAiEditor } from '../hooks/useAiEditor'
// --- React Utils ---
// --- Tiptap Extensions ---
import { AiSuggestionOptions } from '../tiptap-extensions/ai/ai-suggestion'
import '../tiptap-node/code-block-node/code-block-node.scss'
import '../tiptap-node/image-node/image-node.scss'
import '../tiptap-node/list-node/list-node.scss'
import '../tiptap-node/paragraph-node/paragraph-node.scss'
import { DragContextMenu } from '../tiptap-ui/drag-context-menu'
import { EmojiDropdownMenu } from '../tiptap-ui/emoji-dropdown-menu'
import { AICompletionResolver } from './components/ai-selector'
import { AiCommandManager } from './components/ai-command-manager'
import { ButtonManager } from './components/button-manager'
import { InlineImageManager } from './components/inline-image-manager'
import { LinkManager } from './components/link-manager'
import { MainToolbar } from './components/main-toolbar'
import { NotionToolbarFloating } from './components/notion-like-editor-toolbar-floating'
import './styles/_keyframe-animations.scss'
import './styles/_variables.scss'
import { useMemo } from 'react'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

type TiptapAiEditorProps = {
  content: any
  onCreate?: ({ editor }: { editor: Editor }) => void
  onUpdate?: ({ editor }: { editor: Editor }) => void
  readOnly?: boolean
  /**
   * Review mode: content appears readonly but allows text selection for inline comments.
   * When true, editor is technically editable (for selections) but styled as a review document.
   * Use this for auditor workflows where commenting is needed on submitted evidence.
   */
  reviewMode?: boolean
  suggestionOptions?: AiSuggestionOptions & {
    popoverRef?: React.RefObject<HTMLElement | null>
  }
  completionOptions?: {
    completeResolver: AICompletionResolver
    onContinueInChat?: (context: any) => void
  }
  showMainToolbar?: boolean
  enableComments?: boolean
  docId?: string
  workspaceId?: string
  branchId?: string
  changeRequestId?: string
  onCommentsUpdated?: (comments: any[] | Map<string, any>) => void
  onCommentActivated?: (commentId: string | null, comment?: any) => void
  onCommentsDataUpdated?: (comments: Map<string, any> | null) => void
  onCommentsError?: (message: string, error: any) => void
  onContinueInChat?: (context: any) => void
  editorContentClassName?: string  // Allow custom className for EditorContent
  stripCommentMarks?: boolean
}

export function TiptapAiEditor(props: TiptapAiEditorProps) {
  const {
    content,
    onCreate,
    onUpdate,
    readOnly,
    reviewMode = false,
    suggestionOptions,
    showMainToolbar = false,
    completionOptions,
    enableComments = false,
    docId,
    workspaceId,
    branchId,
    changeRequestId,
    onCommentsUpdated,
    onCommentActivated,
    onCommentsDataUpdated,
    onCommentsError,
    onContinueInChat,
    editorContentClassName,
    stripCommentMarks = false,
  } = props

  // In review mode, editor is editable (for text selection) but styled as readonly
  // This allows auditors to add inline comments on submitted evidence
  const effectiveReadOnly = readOnly && !reviewMode

  // Derive a single flag that respects readOnly and required ids
  // Comments are enabled in review mode even if readOnly is true
  const commentsEnabled = !!(
    enableComments && docId && workspaceId && (!readOnly || reviewMode)
  )
  const {
    enabled: commentsEnabledFlag,
    loading: commentsFlagLoading,
  } = useFeatureFlag('comments')

  const commentsConfig = useMemo(() => {
    // Allow comments in review mode even if readOnly is true
    if (!commentsEnabled || !docId || !workspaceId || effectiveReadOnly) {
      return undefined
    }

    return {
      enabled: commentsEnabledFlag && !commentsFlagLoading,
      docId,
      workspaceId,
      branchId,
      changeRequestId,
      onCommentsUpdated,
      onCommentActivated,
      onCommentsDataUpdated,
      onError: onCommentsError,
    }
  }, [
    branchId,
    changeRequestId,
    commentsEnabled,
    commentsEnabledFlag,
    commentsFlagLoading,
    docId,
    onCommentActivated,
    onCommentsDataUpdated,
    onCommentsError,
    onCommentsUpdated,
    effectiveReadOnly,
    workspaceId,
  ])

  const editorRemountKey = commentsEnabled
    ? commentsFlagLoading
      ? 'comments-loading'
      : `comments-ready-${commentsEnabledFlag ? 'on' : 'off'}`
    : 'comments-disabled'

  return (
    <TiptapAiEditorInner
      key={editorRemountKey}
      content={content}
      onCreate={onCreate}
      onUpdate={onUpdate}
      readOnly={effectiveReadOnly}
      reviewMode={reviewMode}
      suggestionOptions={suggestionOptions}
      showMainToolbar={showMainToolbar}
      completionOptions={completionOptions}
      enableComments={enableComments}
      docId={docId}
      workspaceId={workspaceId}
      branchId={branchId}
      changeRequestId={changeRequestId}
      onCommentsUpdated={onCommentsUpdated}
      onCommentActivated={onCommentActivated}
      onCommentsDataUpdated={onCommentsDataUpdated}
      onCommentsError={onCommentsError}
      onContinueInChat={onContinueInChat}
      commentsConfig={commentsConfig}
      editorContentClassName={editorContentClassName}
      stripCommentMarks={stripCommentMarks}
    />
  )
}

type InnerProps = TiptapAiEditorProps & {
  commentsConfig:
    | {
        enabled: boolean
        docId: string
        workspaceId: string
        branchId?: string
        changeRequestId?: string
        onCommentsUpdated?: (comments: any[] | Map<string, any>) => void
        onCommentActivated?: (commentId: string | null, comment?: any) => void
        onCommentsDataUpdated?: (comments: Map<string, any> | null) => void
        onError?: (message: string, error: any) => void
      }
    | undefined
}

function TiptapAiEditorInner({
  content,
  onCreate,
  onUpdate,
  readOnly,
  reviewMode = false,
  suggestionOptions,
  showMainToolbar = false,
  completionOptions,
  enableComments = false,
  onContinueInChat,
  commentsConfig,
  editorContentClassName,
  stripCommentMarks = false,
}: InnerProps) {
  const { editor } = useAiEditor({
    content,
    readOnly: !!readOnly,
    reviewMode,
    onCreate,
    onUpdate,
    extensions: [],
    suggestionOptions,
    commentsConfig,
    stripCommentMarks,
  })

  if (!editor) {
    return null
  }

  // In review mode, hide editing tools but keep the editor interactive for comments
  const hideEditingTools = readOnly || reviewMode

  return (
    <EditorContext.Provider value={{ editor } as EditorContextValue}>
      <div className='editor-container relative w-full'>
        {showMainToolbar && !reviewMode && (
          <div className='editor-toolbar'>
            <MainToolbar editor={editor} />
          </div>
        )}

        <div style={{ display: hideEditingTools ? 'none' : 'block' }}>
          <LinkManager editor={editor} />
          <ButtonManager editor={editor} />
          <InlineImageManager editor={editor} />
          <AiCommandManager
            editor={editor}
            completeResolver={completionOptions?.completeResolver}
          />
        </div>

        <div className='editor-content bg-background relative w-full sm:rounded-lg'>
          <DragContextMenu editor={editor} />

          <EditorContent
            editor={editor}
            readOnly={readOnly}
            className={editorContentClassName || 'mt-4  mb-16'}
            style={{
              cursor: editor.view.dragging ? 'grabbing' : 'auto',
            }}
          >
            <EmojiDropdownMenu />
            {!hideEditingTools && (
              <NotionToolbarFloating
                completeResolver={completionOptions?.completeResolver}
                enableComments={enableComments}
                onContinueInChat={
                  onContinueInChat || completionOptions?.onContinueInChat
                }
              />
            )}
            {/* In review mode, show comment-only toolbar for adding inline comments */}
            {reviewMode && enableComments && (
              <NotionToolbarFloating
                enableComments={true}
                commentOnly={true}
              />
            )}
          </EditorContent>
        </div>
      </div>
    </EditorContext.Provider>
  )
}
